import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { Platform } from '@ionic/angular';
import { Observable, from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})

/**
 * Converts angular client HTTP requests to Native HTTP requests.
 * Reference:
 * https://manuel-heidrich.dev/blog/an-elegant-way-to-handle-native-http-requests-with-cordova-advanced-http-in-an-ionic-app/
 * https://medium.com/@viswakarthikreddy/how-to-convert-angular-http-to-native-http-in-ionic-ac447efe4579
 */
export class NativeHttpInterceptor implements HttpInterceptor {
  // if we are on a device (which we are determining by checking if platform is cordova or capacitor) then we should use
  // nativehttp instead of angular's http calls
  private useNativeHttp: boolean = this.platform.is('cordova') || this.platform.is('capacitor');

  constructor(
    protected platform: Platform,
    private nativeHttp: HTTP,
  ) {}

  /**
   * This intercept function is used to identify the native HTTP request.
   *
   * @param request - Represents the request on its way to the server.
   * @param next - Represents next step in the interceptor chain.
   * @returns - Observable that wraps up an HttpEvent object.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.useNativeHttp) {
      return from(this.handleNativeRequest(request));
    }

    // If we aren't using native http, just continue with the request as usual
    return next.handle(request);
  }

  /**
   * handleNativeRequest function will convert and send the request.
   * In the response we will receive a parsed JSON body or an error message.
   *
   * @param request - the original http request to convert to native
   * @returns http request, as the original http request would return
   */
  private async handleNativeRequest(request: HttpRequest<any>): Promise<HttpResponse<any>> {
    // attach the headers to the request
    const headerKeys = request.headers.keys();
    const headers = {};

    /**
     * Some external APIs we need to use require a serializer other than 'json'. To work around this,
     * we use a Set-Serializer header in the outgoing api request to let us know what we should set the header to.
     * This header is not included in the actual API call, it is just used as a way to communicate this serializer override requirement.
     */
    let serializerOverride;

    headerKeys.forEach((key) => {
      // let's check to see if we've been sent a header to determine what serializer we should be using.
      if (key === 'Set-Serializer') {
        serializerOverride = request.headers.get(key);
      } else {
        // if it's not the serializer override header, add it to the request!
        headers[key] = request.headers.get(key);
      }
    });

    try {
      await this.platform.ready();

      const method = request.method.toLowerCase();

      // build the request object
      const nativeRequestObject = {
        method: <any>method,
        headers,
        data: request.body,
        serializer: serializerOverride || 'json',
      };

      // if the body of the request is empty, set it to an empty object.
      // otherwise the native http library will throw an error.
      // see here: https://github.com/silkimen/cordova-plugin-advanced-http/issues/177
      if (!request.body) {
        nativeRequestObject['data'] = {};
      }

      // ES-15086: Increasing timeout value to resolve dashboard timeout issue for certail support workers.
      // According to docs default should be 60 but its on checking it return 8.
      this.nativeHttp.setRequestTimeout(60);
      // make the request
      const nativeHttpResponse = await this.nativeHttp.sendRequest(request.urlWithParams, nativeRequestObject);

      // now format the response to match our angular client http response
      let body;

      // attempt to JSON parse our body, if it fails, skip parsing
      try {
        body = JSON.parse(nativeHttpResponse.data);
      } catch (error) {
        body = { response: nativeHttpResponse.data };
      }

      const response = new HttpResponse({
        body,
        status: nativeHttpResponse.status,
        headers: <HttpHeaders>(<any>nativeHttpResponse.headers),
        url: nativeHttpResponse.url,
      });

      return Promise.resolve(response);
    } catch (error) {
      if (!error.status) {
        return Promise.reject(error);
      }

      const response = new HttpErrorResponse({
        error: typeof error.error === 'object' ? JSON.parse(error.error) : error,
        status: error.status,
        headers: error.headers,
        url: error.url,
        statusText: error.statusText,
      });

      // HTTPErrorResponse.message is readonly, so we gotta use this 'as any' hack to let us write the message
      response['message' as any] = error.message;

      return Promise.reject(response);
    }
  }
}
